// CP Quality Pools proposal content and math, shared by the bid editor,
// the inground calculator, and proposalPDF.js. Pure functions only.

export var DEFAULT_MATERIALS_DEPOSIT = 15000
export var DEFAULT_WATER_MITIGATION = 2500
export var DEFAULT_RETAINER_PCT = 15
export var PROPOSAL_VALID_DAYS = 30

export var DEFAULT_SCOPE_ITEMS = [
  'Layout, staking, and site protection',
  'MISS DIG public utility locate',
  'Excavation to design profile',
  'Spoils graded and left on site',
  'Poured structural concrete footing',
  'Steel wall panel assembly, bracing, and plumb-and-square set',
  'Vermiculite floor, hand-troweled',
  'Plumbing: skimmers, returns, and anti-entrapment main drains, pressure-tested',
  'Backfill and compaction at all walls',
  'Custom liner installation and set during fill',
  'Equipment pad, set, and system startup',
  'Conduit run to the equipment pad for the electrician',
  'Initial water fill',
  'Final walkthrough and owner orientation',
]

export var DEFAULT_EQUIPMENT_ITEMS = [
  'Variable-speed circulation pump',
  'High-rate sand filter with multiport valve',
  'In-line chlorinator',
  'Two (2) wide-mouth skimmers',
  'Three (3) return jets, adjustable eyeballs',
  'Two (2) main drains, ISPSC-compliant anti-entrapment covers',
]

// Site-condition exclusions that apply to every inground job. The calculator
// puts its own not-included items (fence, patio, gas line...) ahead of these.
export var STANDARD_NOT_INCLUDED = [
  'Off-site haul-away of excavated spoils',
  'Retaining walls or significant grade correction',
  'Landscaping, sod, irrigation repair, or final grading beyond the work area',
  'Removal of rock, buried debris, or unsuitable soils',
]

export var DEFAULT_COPING_OPTIONS = [
  { label: 'Standard bullnose coping', price_adjustment: 0, is_base: true },
  { label: 'Cantilever concrete coping', price_adjustment: 1500, is_base: false },
  { label: 'Paver coping', price_adjustment: 4500, is_base: false },
  { label: 'Natural stone coping (bluestone / limestone)', price_adjustment: 6000, is_base: false },
]

export var BUILD_STAGES = [
  { phase: 'Permit & layout', what: 'Plot plan submitted, permit pulled, MISS DIG locate, pool staked on site with you present.' },
  { phase: 'Excavation & footing', what: 'Dig to the design profile, set and pour the structural footing.' },
  { phase: 'Wall set & plumbing', what: 'Steel panels assembled, braced, and squared. Stairs set. Plumbing run and pressure-tested.' },
  { phase: 'Backfill & inspection', what: 'Backfill and compaction, conduit run, rough inspection.' },
  { phase: 'Concrete & electrical', what: 'Deck poured and finished with your coping selection. Licensed electrician bonds and wires the equipment.' },
  { phase: 'Floor, liner & fill', what: 'Vermiculite floor troweled, liner set and locked in during the water fill.' },
  { phase: 'Fence & handoff', what: 'Safety fence installed, final inspection, system startup, and owner orientation.' },
]
export var BUILD_STAGES_NOTE = 'Sequence is typical. Actual duration depends on weather, inspection scheduling, and concrete cure time — we will give you a working schedule at contract signing.'

export var NON_DIVING_NOTE = 'This is a non-diving pool. No diving board, slide, or platform may be installed, and "No Diving" markers will be recommended at the deck.'

export var PROPOSAL_TERMS = 'Terms & Conditions - CP Quality Pools\n\n'
  + '1. Proposal validity. This proposal is valid for 30 days from the date issued. Material pricing is subject to change after that date.\n'
  + '2. Site access. Owner shall provide clear access for excavation equipment and delivery trucks. Damage to lawns, drives, walks, or underground private utilities (sprinklers, invisible fence, septic, private lines) resulting from normal access and construction is not the responsibility of the contractor. Public utilities will be located through MISS DIG prior to digging.\n'
  + '3. Permits. Contractor will pull the building permit and prepare the plot plan as quoted. Owner is responsible for compliance with any HOA, deed, or easement restrictions.\n'
  + '4. Unforeseen conditions. Rock, buried debris, unstable soils, or other concealed conditions requiring additional work will be documented and quoted as a written change order before the work proceeds. Water conditions are governed by the water mitigation clause in this proposal.\n'
  + '5. Schedule. Start and completion dates are estimates and subject to weather, inspection scheduling, and material availability.\n'
  + '6. Warranty. Workmanship is warranted for one (1) year from substantial completion. Pool kit, liner, and equipment carry their respective manufacturers\' warranties, which are passed through to the owner. Warranty excludes damage from improper water chemistry, freezing, neglect, or allowing the water level to drop below the skimmer.\n'
  + '7. Owner responsibilities. Owner is responsible for maintaining water level and chemistry from the date of fill forward, and for keeping the pool secured until the safety fence is complete and inspected.\n'
  + '8. Change orders. Any change to the scope, materials, or quantities above must be authorized in writing by both parties before the work is performed.'

export var LIEN_NOTICE = 'Under the Michigan Construction Lien Act, a contractor, subcontractor, supplier, or laborer who provides an improvement to real property and is not paid may have a right to a construction lien against the property. Owner should retain all payment records and lien waivers. A Notice of Furnishing and sworn statements will be provided as required by law.'

export function waterMitigationText(amount) {
  return 'If groundwater, a high water table, or persistent surface water is encountered during excavation or construction and requires dewatering, well-pointing, sump installation, or stone stabilization to complete the work, a **one-time charge of ' + money(amount) + '** will be added to the contract price. This charge applies only once and only if such conditions are actually encountered. **It is not included in the project total above.** No water mitigation work will begin without notifying the owner first.'
}

function num(v) { var n = parseFloat(v); return isNaN(n) ? 0 : n }
function round2(n) { return Math.round(n * 100) / 100 }
export function money(n) {
  return '$' + num(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// ---- Proposal numbers: CPQ-YYMMDD-XX ----

export function clientInitials(name) {
  var words = String(name || '').split(/[^A-Za-z]+/).filter(Boolean)
  if (words.length === 0) return 'XX'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

export function formatProposalNumber(clientName, isoDate) {
  var d = String(isoDate || '').slice(0, 10).split('-')
  return 'CPQ-' + d[0].slice(2) + d[1] + d[2] + '-' + clientInitials(clientName)
}

// Given the base number and the numbers already taken, return a free one:
// CPQ-260827-DD, then CPQ-260827-DD-2, -3...
export function uniqueProposalNumber(base, taken) {
  var set = new Set(taken || [])
  if (!set.has(base)) return base
  for (var i = 2; ; i++) { if (!set.has(base + '-' + i)) return base + '-' + i }
}

export function todayISO() {
  var d = new Date()
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

export function addDaysISO(isoDate, days) {
  var d = new Date(isoDate + 'T12:00:00')
  d.setDate(d.getDate() + days)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

// Date shown as "Date Issued" when the bid has no issued_date of its own.
export function issuedDateFor(bid) {
  return bid.issued_date || String(bid.quote_sent_at || bid.created_at || todayISO()).slice(0, 10)
}

// ---- Payment schedule ----
// 1. Pool kit cost, due when the kit order is processed
// 2. Materials deposit, due the day installation begins
// 3. Everything else except the retainer, due when the water goes in
// 4. Retainer (% of contract price), due at substantial completion
// The water-fill payment is computed as the remainder so the rows always sum
// to the contract price exactly.
export function paymentSchedule(bid, contractPrice) {
  var total = round2(num(contractPrice != null ? contractPrice : bid.total))
  var kit = bid.kit_deposit_amount != null && bid.kit_deposit_amount !== '' ? round2(num(bid.kit_deposit_amount)) : null
  var materials = bid.materials_deposit_amount != null && bid.materials_deposit_amount !== '' ? round2(num(bid.materials_deposit_amount)) : DEFAULT_MATERIALS_DEPOSIT
  var retainerPct = bid.retainer_pct != null && bid.retainer_pct !== '' ? num(bid.retainer_pct) : DEFAULT_RETAINER_PCT
  var retainer = round2(total * retainerPct / 100)
  var waterFill = round2(total - (kit || 0) - materials - retainer)

  var rows = []
  if (kit != null) rows.push({ key: 'kit', milestone: 'Pool Kit Order', due: 'Due when your pool kit order is processed.', amount: kit })
  rows.push({ key: 'materials', milestone: 'Materials Deposit', due: 'Due the day installation begins.', amount: materials })
  rows.push({ key: 'water_fill', milestone: 'Water Fill', due: 'Balance less the ' + fmtPct(retainerPct) + ' retainer, due when the water goes in.', amount: waterFill })
  rows.push({ key: 'retainer', milestone: 'Retainer (' + fmtPct(retainerPct) + ')', due: 'Due upon substantial completion of the work.', amount: retainer })

  var warnings = []
  if (kit == null) warnings.push('Pool kit cost is not set, so the kit order payment is left off the schedule.')
  if (waterFill < 0) warnings.push('Kit, materials deposit, and retainer add up to more than the contract price.')
  return { total: total, retainerPct: retainerPct, rows: rows, warnings: warnings }
}

function fmtPct(p) { return (Math.round(p * 100) / 100) + '%' }

// ---- Coping options ----

export function sortOptions(options) {
  return (options || []).slice().sort(function(a, b) {
    if (!!b.is_base !== !!a.is_base) return a.is_base ? -1 : 1
    return (a.sort_order || 0) - (b.sort_order || 0)
  })
}

// Each option with the project total it produces and the water-fill/retainer
// payments that change with it.
export function optionTotals(bid, options) {
  var base = num(bid.total)
  return sortOptions(options).map(function(o) {
    var adj = num(o.price_adjustment)
    var total = round2(base + adj)
    var sched = paymentSchedule(bid, total)
    var byKey = {}
    sched.rows.forEach(function(r) { byKey[r.key] = r.amount })
    return { option: o, adjustment: adj, total: total, waterFill: byKey.water_fill, retainer: byKey.retainer }
  })
}

// ---- Terms ----
// Accepts both the template style ("1. Title. Body") and the older style
// where the title sits alone on the numbered line and the body follows.
export function parseTerms(text) {
  var items = []
  var preamble = []
  var cur = null
  String(text || '').split('\n').forEach(function(raw) {
    var line = raw.trim()
    if (!line) return
    if (/^terms\s*&\s*conditions/i.test(line) && !items.length && !cur) return
    var m = line.match(/^(\d+)\.\s+(.*)$/)
    if (m) {
      if (cur) items.push(cur)
      var rest = m[2]
      var dot = rest.indexOf('. ')
      if (dot > 0 && dot < 60) cur = { n: m[1], title: rest.slice(0, dot), body: rest.slice(dot + 2) }
      else cur = { n: m[1], title: rest.replace(/\.$/, ''), body: '' }
    } else if (cur) {
      cur.body = cur.body ? cur.body + ' ' + line : line
    } else {
      preamble.push(line)
    }
  })
  if (cur) items.push(cur)
  return { preamble: preamble.join(' '), items: items }
}

// ---- Inground calculator -> proposal content ----

function trimNum(v) { var n = parseFloat(v); return isNaN(n) ? '' : String(Math.round(n * 100) / 100) }

export function feetInches(inches) {
  var n = Math.round(parseFloat(inches) || 0)
  var ft = Math.floor(n / 12), inch = n % 12
  return ft + '′' + (inch ? inch + '″' : '')
}

var SHAPE_WORDS = { rectangle: 'rectangle', l_shape: 'L-shape', oval: 'oval', grecian: 'grecian', freeform: 'freeform' }

// "16′ × 32′ rectangle"
export function poolSizeText(shape, dims) {
  var word = SHAPE_WORDS[shape] || shape || ''
  var l = trimNum(dims && dims.length), w = trimNum(dims && dims.width)
  if (shape === 'freeform') return 'freeform' + (trimNum(dims && dims.perimeter) ? ' (' + trimNum(dims.perimeter) + '′ perimeter)' : '')
  if (!l || !w) return word
  return w + '′ × ' + l + '′ ' + word
}

function joinWords(list) {
  if (list.length <= 1) return list.join('')
  return list.slice(0, -1).join(', ') + ', and ' + list[list.length - 1]
}

function unitLabel(unit) {
  var u = String(unit || '').trim().replace(/^per\s+/i, '')
  if (/^(ft|foot|feet|lf)$/i.test(u)) return 'lin ft'
  if (/^(sqft|sq ft|sf)$/i.test(u)) return 'sq ft'
  return u || 'ea'
}

// calc: { shape, dims, inputs, addons, rates, exclusions, costs, hardCost, price }
// Returns bid_lines rows plus the bid fields the proposal reads.
export function buildIngroundProposal(calc) {
  var a = calc.addons || {}
  var costs = calc.costs || {}
  var gravel = calc.inputs && calc.inputs.backfill_type === 'gravel'
  var size = poolSizeText(calc.shape, calc.dims)

  var features = []
  if (a.stairs) features.push('entry stairs')
  if (a.sun_shelf) features.push('sun shelf')
  if (a.deep_end) features.push('deep end')
  if (a.auto_cover) features.push('automatic safety cover')
  if (a.heater) features.push('heater')
  ;(a.custom_items || []).forEach(function(c) { if (c.desc && parseFloat(c.cost) > 0) features.push(c.desc) })

  var includes = []
  if (costs.excavation > 0) includes.push('excavation')
  if (costs.footing > 0) includes.push('poured footing')
  includes.push('steel wall set')
  if (costs.plumbing > 0) includes.push('plumbing')
  if (costs.backfill > 0) includes.push((gravel ? 'aggregate' : 'sand') + ' backfill')
  if (costs.verm > 0) includes.push('vermiculite floor')
  if (costs.labor > 0) includes.push('installation labor')
  if (costs.water > 0) includes.push('initial water fill')

  var exclusions = calc.exclusions || []
  var siteLines = []
  exclusions.forEach(function(e) {
    if (!e.included || !e.label) return
    var rate = parseFloat(e.rate), qty = parseFloat(e.qty)
    if (rate > 0 && qty > 0) {
      siteLines.push({
        description: e.label + (e.custom_note ? ' — ' + e.custom_note : ''),
        category: 'inground', section: 'site_work',
        quantity: qty, unit: unitLabel(e.unit), unit_cost: round2(rate), total_cost: round2(qty * rate),
      })
    } else {
      includes.push(e.label.toLowerCase() + (e.custom_note ? ' (' + e.custom_note + ')' : ''))
    }
  })

  var baseLine = {
    description: 'Pool construction — turnkey',
    detail: size + ' vinyl liner inground pool' + (features.length ? ', ' + features.join(', ') : '') + '. Includes ' + joinWords(includes) + '.',
    category: 'inground', section: 'base',
    quantity: 1, unit: 'ea', unit_cost: round2(calc.price), total_cost: round2(calc.price), actual_cost: round2(calc.hardCost),
  }

  var shallow = feetInches(calc.rates && calc.rates.standard_depth_inches ? calc.rates.standard_depth_inches : 42)
  var specs = [{ label: 'Size & shape', value: size }]
  specs.push({ label: 'Depth profile', value: a.deep_end ? shallow + ' shallow end sloping to a deep end' : shallow + ' throughout' })
  if (a.stairs) specs.push({ label: 'Entry', value: 'polymer entry stairs' })
  if (a.sun_shelf) specs.push({ label: 'Feature', value: 'sun shelf' })
  specs.push({ label: 'Structure', value: 'galvanized steel wall panel system on a poured concrete footing' })
  specs.push({ label: 'Floor', value: 'troweled vermiculite over compacted base' })
  specs.push({ label: 'Finish', value: 'custom-fit vinyl liner' })
  specs.push({ label: 'Backfill', value: gravel ? 'crushed aggregate at all walls' : 'sand at all walls' })

  var scope = DEFAULT_SCOPE_ITEMS.slice()
  var extra = []
  if (a.stairs) extra.push('Entry stairs set and gravel-packed')
  if (a.sun_shelf) extra.push('Sun shelf formed')
  if (a.deep_end) extra.push('Deep end excavated and formed')
  scope.splice(scope.indexOf('Vermiculite floor, hand-troweled'), 0, ...extra)
  scope = scope.map(function(s) { return s === 'Backfill and compaction at all walls' ? (gravel ? 'Aggregate' : 'Sand') + ' backfill and compaction at all walls' : s })

  var notIncluded = []
  exclusions.forEach(function(e) {
    if (e.included || !e.label) return
    notIncluded.push(e.label + (parseFloat(e.rate) > 0 ? ' (available — $' + e.rate + ' ' + (e.unit || '').trim() + ')' : '') + (e.custom_note ? ' — ' + e.custom_note : ''))
  })
  if (!a.heater) notIncluded.push('Pool heater (available — quoted separately)')
  if (!a.auto_cover) notIncluded.push('Automatic safety cover (available — quoted separately)')
  // Skip standard items the calculator list already covers (e.g. "Landscaping").
  var firstWord = function(s) { return s.split(/[\s/,]+/)[0].toLowerCase() }
  STANDARD_NOT_INCLUDED.forEach(function(s) {
    if (!notIncluded.some(function(n) { return firstWord(n) === firstWord(s) })) notIncluded.push(s)
  })

  var l = trimNum(calc.dims && calc.dims.length), w = trimNum(calc.dims && calc.dims.width)
  var jobName = (l && w && calc.shape !== 'freeform' ? w + '′ × ' + l + '′ ' : '')
    + (calc.shape && calc.shape !== 'rectangle' ? (SHAPE_WORDS[calc.shape] || calc.shape).replace(/^./, function(c) { return c.toUpperCase() }) + ' ' : '')
    + 'Inground Pool'

  var overviewFeatures = features.filter(function(f) { return f !== 'deep end' })
  return {
    lines: [baseLine].concat(siteLines),
    fields: {
      quote_format: 'proposal',
      pool_specs: specs,
      pool_specs_note: NON_DIVING_NOTE,
      scope_items: scope,
      equipment_items: DEFAULT_EQUIPMENT_ITEMS.slice(),
      not_included: notIncluded,
      water_mitigation_amount: DEFAULT_WATER_MITIGATION,
    },
    jobName: jobName,
    overview: 'Thank you for the opportunity to quote your new swimming pool. This proposal covers the complete installation of a **'
      + size + ' vinyl liner inground pool**' + (overviewFeatures.length ? ' with ' + joinWords(overviewFeatures.map(function(f) { return '**' + f + '**' })) : '')
      + (siteLines.length ? ', along with the site work itemized below' : '') + ' — ready to swim.',
  }
}

// ---- Editor helpers: lists stored as text[] / [{label, value}] ----

export function listToText(list) { return (list || []).join('\n') }
export function textToList(text) {
  return String(text || '').split('\n').map(function(s) { return s.trim() }).filter(Boolean)
}
export function specsToText(specs) {
  return (specs || []).map(function(s) { return s.label ? s.label + ': ' + (s.value || '') : (s.value || '') }).join('\n')
}
export function textToSpecs(text) {
  return textToList(text).map(function(line) {
    var i = line.indexOf(':')
    return i > 0 ? { label: line.slice(0, i).trim(), value: line.slice(i + 1).trim() } : { label: '', value: line }
  })
}
